/**
 * The data behind a generated tournament banner: the initials it draws, and the
 * layout it draws them in (spec `2026-08-16-tournament-fallback-banner-design.md`).
 *
 * Pure and browser-only. `packages/shared` is where logic goes when the API and
 * the client must agree on it — nothing on the server has any use for a
 * monogram — so this sits beside `tournamentSearch.ts` and `gameLocation.ts`.
 */

/**
 * Words too common to identify a tournament. English only, deliberately: this
 * is a display nicety, and a list reaching for every language would be wrong in
 * more interesting ways than it is useful.
 */
const STOPWORDS = new Set(["a", "an", "and", "at", "for", "in", "of", "on", "the"]);

/** Anything that is not a letter or a digit separates words. */
const SEPARATORS = /[^\p{L}\p{N}]+/u;

const STARTS_WITH_DIGIT = /^\p{N}/u;

/**
 * Up to two uppercase letters standing in for a tournament name, or `""` when
 * the name has no letters to take — the caller draws a mark for that case
 * rather than an empty box.
 *
 * Tokens starting with a digit come out first: a year or an age bracket leads a
 * great many tournament names and distinguishes none of them, so
 * "2026 Summer Classic" and "12U Fall Invitational" want their later words.
 */
export function tournamentInitials(name: string): string {
  const words = name.split(SEPARATORS).filter((w) => w.length > 0 && !STARTS_WITH_DIGIT.test(w));
  // Stopwords come out only if something survives them: "The Open" is OP, but a
  // name that is nothing but stopwords still has to draw something.
  const significant = words.filter((w) => !STOPWORDS.has(w.toLowerCase()));
  const chosen = significant.length > 0 ? significant : words;

  if (chosen.length === 0) return "";
  // Array.from, not [0] — a name opening outside the BMP would otherwise be cut
  // through the middle of a surrogate pair.
  if (chosen.length === 1) return Array.from(chosen[0]!).slice(0, 2).join("").toUpperCase();
  return chosen
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? "")
    .join("")
    .toUpperCase();
}

/* --------------------------------- layout --------------------------------- */

export interface BannerVariant {
  /** Which corner the darker wedge fills. */
  corner: "tl" | "tr" | "bl" | "br";
  /** Wedge width as a fraction of the box width. */
  depth: number;
  /** Dot-grid phase in user units, so two banners never line their texture up. */
  phase: number;
}

/**
 * The monogram is centered and the wedge is a corner triangle; past this depth
 * the two would meet. See the test — it is a layout invariant, not a taste call.
 */
export const MAX_WEDGE_DEPTH = 0.38;

/**
 * Six is enough that a three-across grid rarely repeats itself, and few enough
 * that the whole set can be eyeballed on one screen.
 */
export const BANNER_VARIANTS: readonly BannerVariant[] = [
  { corner: "br", depth: 0.34, phase: 0 },
  { corner: "tr", depth: 0.28, phase: 5 },
  { corner: "bl", depth: 0.3, phase: 9 },
  { corner: "tl", depth: 0.24, phase: 3 },
  { corner: "br", depth: 0.22, phase: 12 },
  { corner: "tr", depth: 0.38, phase: 7 },
];

/**
 * FNV-1a, 32-bit. Written out rather than pulled from a package: it is six
 * lines, and a hash that changed under us would restyle every banner in the app
 * at once.
 */
export function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // imul keeps the 32-bit multiply exact; `*` would lose the low bits to float.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * The layout for a tournament, seeded by its id and never by its name — a
 * director renaming an event should not reshuffle its artwork.
 */
export function bannerVariant(seed: string): BannerVariant {
  return BANNER_VARIANTS[fnv1a(seed) % BANNER_VARIANTS.length]!;
}
